import { ProjectPanelRoute } from "@/types/routes";

/** True for the two tabs that show the same active shot's media (Frame stills vs. Video) - see
 * `targetPanelForMainSpaceLibraryDrop` below for why this matters for drag-and-drop. */
export function isFrameOrVideoPanel(panel: string | undefined): boolean {
  return panel === ProjectPanelRoute.ImageFrame || panel === ProjectPanelRoute.Video;
}

/** Library card → main space / timeline slot → main space (image entity id). */
export const AI_STUDIO_LIBRARY_IMAGE_DRAG_MIME = "application/x-ai-studio-library-image";

/** When present, drag originated from a completed shot video row (`visuals[].video_id`). */
export const AI_STUDIO_LIBRARY_VIDEO_DRAG_MIME = "application/x-ai-studio-library-video";

/** Timeline slot reorder: payload is the 0-based source index string (not an image id). */
export const AI_STUDIO_TIMELINE_REORDER_INDEX_MIME = "application/x-ai-studio-timeline-reorder-index";

export type LibraryDragMediaRestriction = "image-only" | "video-only";

/** Panel-based rules for library → main space / timeline (imageFrame vs video tools). */
export function libraryDragRestrictionForPanel(panel: string | undefined): LibraryDragMediaRestriction | null {
  if (panel === ProjectPanelRoute.ImageFrame) {
    return "image-only";
  }

  if (panel === ProjectPanelRoute.Video) {
    return "video-only";
  }

  return null;
}

export function hasLibraryVideoId(videoId: string | null | undefined): boolean {
  return typeof videoId === "string" && videoId.trim().length > 0;
}

export function isLibraryDragAllowedForRestriction(
  restriction: LibraryDragMediaRestriction,
  videoId: string | null | undefined
): boolean {
  const hasVideo = hasLibraryVideoId(videoId);
  if (restriction === "image-only") {
    return !hasVideo;
  }

  return hasVideo;
}

/** True when the drag payload includes a completed-shot video id (uses `types` — safe during `dragover`). */
export function isLibraryVideoDragData(dataTransfer: DataTransfer): boolean {
  return Array.from(dataTransfer.types).includes(AI_STUDIO_LIBRARY_VIDEO_DRAG_MIME);
}

export function isLibraryDragAllowedForPanel(panel: string | undefined, dataTransfer: DataTransfer): boolean {
  const restriction = libraryDragRestrictionForPanel(panel);
  if (restriction === null || !isLibraryImageDragData(dataTransfer)) {
    return true;
  }

  const hasVideo = isLibraryVideoDragData(dataTransfer);
  if (restriction === "image-only") {
    return !hasVideo;
  }

  return hasVideo;
}

/**
 * Frame and Video are two views onto the same shot, so a MainSpace canvas drop of the "wrong"
 * media kind (e.g. a video-tagged library card dropped while on the Frame tab) should still apply
 * - just against the matching tab - rather than being rejected outright. Returns the panel the
 * drop should end up showing (possibly the panel already open, meaning no navigation is needed),
 * or `null` outside Frame/Video (e.g. Characters/Locations never redirect).
 */
export function targetPanelForMainSpaceLibraryDrop(panel: string | undefined, hasVideoId: boolean): string | null {
  if (!isFrameOrVideoPanel(panel)) {
    return null;
  }

  return hasVideoId ? ProjectPanelRoute.Video : ProjectPanelRoute.ImageFrame;
}

export function setLibraryImageDragData(dataTransfer: DataTransfer, imageId: string, videoId?: string | null): void {
  dataTransfer.setData("text/plain", imageId);
  dataTransfer.setData(AI_STUDIO_LIBRARY_IMAGE_DRAG_MIME, imageId);
  const v = typeof videoId === "string" ? videoId.trim() : "";
  if (v.length > 0) {
    dataTransfer.setData(AI_STUDIO_LIBRARY_VIDEO_DRAG_MIME, v);
  }
}

export function isLibraryImageDragData(dataTransfer: DataTransfer): boolean {
  return Array.from(dataTransfer.types).includes(AI_STUDIO_LIBRARY_IMAGE_DRAG_MIME);
}

export function isExternalLibraryImageDragData(dataTransfer: DataTransfer): boolean {
  return isLibraryImageDragData(dataTransfer) && !Array.from(dataTransfer.types).includes(AI_STUDIO_TIMELINE_REORDER_INDEX_MIME);
}

export function readLibraryImageIdFromDataTransfer(dataTransfer: DataTransfer): string | undefined {
  const imageId = (dataTransfer.getData("text/plain") || dataTransfer.getData(AI_STUDIO_LIBRARY_IMAGE_DRAG_MIME)).trim();
  return imageId.length > 0 ? imageId : undefined;
}

export function readLibraryVideoIdFromDataTransfer(dataTransfer: DataTransfer): string | undefined {
  if (!isLibraryVideoDragData(dataTransfer)) {
    return undefined;
  }

  const videoId = dataTransfer.getData(AI_STUDIO_LIBRARY_VIDEO_DRAG_MIME).trim();
  return videoId.length > 0 ? videoId : undefined;
}

export function readLibraryDragFromDataTransfer(dataTransfer: DataTransfer): { imageId?: string; videoId?: string } {
  const imageId = readLibraryImageIdFromDataTransfer(dataTransfer);
  const videoId = readLibraryVideoIdFromDataTransfer(dataTransfer);
  return { ...(imageId !== undefined ? { imageId } : {}), ...(videoId !== undefined ? { videoId } : {}) };
}
